import AlertIcon from '@assets/icons/alert.svg';
import CameraIcon from '@assets/icons/camera.svg';
import i18n from '@/utils/i18n';
import misnapModule, { MiSnapErrorCode } from '@modules/expo-misnap';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Button } from '@/components/button';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { getCheckCaptureDeviceTracking } from '@/utils/check-capture-device-tracking';
import {
  Image,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  selectCredentials,
  selectImages,
  useCheckCaptureStore,
} from '@/stores/check-capture-store';
import { Typography } from '@usepepper/ui/atoms/typography';
import { usePrepareCheckCapture } from '@/hooks/payments/use-prepare-check-capture';
import { useRequestCamera } from '@/hooks/use-request-camera';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';

export interface Props {
  chatUUID: string;
  currencyCode: string | null;
  onUnavailable: (message: string) => void;
  onUseCheck: () => void;
  subtotalInMicros: number;
}

export const CheckCaptureContent: React.FC<Props> = ({
  chatUUID,
  currencyCode,
  onUnavailable,
  onUseCheck,
  subtotalInMicros,
}) => {
  const misnap = misnapModule;
  const [styles, theme] = useStyles();
  const { sk } = useTheme();
  const { bottom } = useSafeAreaInsets();
  const { showErrorToast } = useToasts();
  const credentials = useCheckCaptureStore(selectCredentials);
  const images = useCheckCaptureStore(selectImages);
  const imageValidationError = useCheckCaptureStore(
    (s) => s.submission.imageValidationError,
  );
  const clearImageValidationError = useCheckCaptureStore(
    (s) => s.clearImageValidationError,
  );
  const setCredentials = useCheckCaptureStore((s) => s.setCredentials);
  const setImages = useCheckCaptureStore((s) => s.setImages);
  const [analysisError, setAnalysisError] = useState<{
    front: boolean;
    rear: boolean;
  }>({ front: false, rear: false });
  const formattedAmount = formatCurrency(subtotalInMicros, currencyCode);

  const [prepareCheckCapture, { loading: prepareLoading }] =
    usePrepareCheckCapture();
  const { requestCamera } = useRequestCamera();

  useEffect(() => {
    if (!misnap) {
      onUnavailable(i18n.t('check_capture.unavailable'));
    }
  }, [misnap, onUnavailable]);

  useEffect(() => {
    if (credentials !== null || !misnap) {
      return;
    }
    prepareCheckCapture({
      onCompleted: (data) => {
        const {
          front_s3_key,
          front_upload_url,
          misnap_license,
          rear_s3_key,
          rear_upload_url,
        } = data.prepareCheckCapture;
        if (
          front_s3_key &&
          front_upload_url &&
          misnap_license &&
          rear_s3_key &&
          rear_upload_url
        ) {
          misnap.setLicenseKey(misnap_license);
          setCredentials({
            frontS3Key: front_s3_key,
            frontUploadUrl: front_upload_url,
            rearS3Key: rear_s3_key,
            rearUploadUrl: rear_upload_url,
          });
        } else {
          onUnavailable(i18n.t('check_capture.prepare_failed'));
        }
      },
      onError: () => {
        onUnavailable(i18n.t('check_capture.prepare_failed'));
      },
      variables: {
        chatUUID,
        deviceTracking: getCheckCaptureDeviceTracking(),
      },
    });
  }, [
    chatUUID,
    credentials,
    misnap,
    onUnavailable,
    prepareCheckCapture,
    setCredentials,
  ]);

  const handleCapture = async (side: 'front' | 'rear') => {
    if (!credentials || !misnap) {
      return;
    }
    const cameraStatus = await requestCamera();
    if (cameraStatus !== 'granted') {
      return;
    }
    try {
      const result = await misnap.captureCheck(
        side === 'rear' ? 'back' : 'front',
      );
      setImages({
        ...images,
        [side === 'front' ? 'frontImage' : 'rearImage']: result.image,
      });
      setAnalysisError((prev) => ({ ...prev, [side]: false }));
      clearImageValidationError(side);
    } catch (e) {
      const code = (e as { code?: string })?.code;
      if (code === MiSnapErrorCode.CANCELLED) {
        return;
      }
      if (code === MiSnapErrorCode.LICENSE_INVALID) {
        onUnavailable(i18n.t('check_capture.license_expired'));
        return;
      }
      if (code === MiSnapErrorCode.ANALYSIS) {
        setAnalysisError((prev) => ({ ...prev, [side]: true }));
        return;
      }
      showErrorToast();
    }
  };

  const bothSidesCaptured = Boolean(images.frontImage && images.rearImage);

  const sideError = (side: 'front' | 'rear'): string | null => {
    if (analysisError[side]) {
      return i18n.t('check_capture.analysis_error');
    }
    if (imageValidationError?.[side]) {
      return i18n.t('check_capture.side_validation_error');
    }
    return null;
  };
  const frontError = sideError('front');
  const rearError = sideError('rear');

  const imageUri = (encoded: string) =>
    encoded.startsWith('data:') ? encoded : `data:image/jpeg;base64,${encoded}`;

  if ((!credentials || prepareLoading) && misnap) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <View
      style={[
        styles.base,
        { marginBottom: Math.max(bottom, sk.spacing.spaceLarge) },
      ]}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.section}>
          <Typography variant="heading-sm">
            {i18n.t('check_capture.amount_due')}
          </Typography>
          <Typography variant="paragraph-base-600">
            {formattedAmount}
          </Typography>
        </View>

        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-md"
        >
          {i18n.t('check_capture.helper')}
        </Typography>

        <View style={styles.section}>
          <Typography variant="heading-sm">
            {i18n.t('check_capture.front_of_check')}
          </Typography>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => handleCapture('front')}
            style={styles.card}
            testID="check-capture-front-card"
          >
            {images.frontImage ? (
              <Image
                resizeMode="cover"
                source={{ uri: imageUri(images.frontImage) }}
                style={styles.checkImage}
              />
            ) : null}
            <CameraIcon
              height={24}
              stroke={
                images.frontImage
                  ? theme.sk.color.icon.positive.primary
                  : theme.sk.color.icon.tertiary
              }
              width={24}
            />
            <Typography
              color={
                images.frontImage
                  ? theme.sk.color.text.success
                  : theme.sk.color.text.tertiary
              }
              variant="paragraph-md"
            >
              {images.frontImage
                ? i18n.t('check_capture.tap_to_recapture')
                : i18n.t('check_capture.tap_to_capture')}
            </Typography>
          </TouchableOpacity>
          {frontError ? (
            <View style={styles.errorRow}>
              <AlertIcon
                height={16}
                stroke={theme.sk.color.icon.negative.primary}
                width={16}
              />
              <Typography
                color={theme.sk.color.text.error}
                variant="paragraph-sm"
              >
                {frontError}
              </Typography>
            </View>
          ) : null}
        </View>

        <View style={styles.section}>
          <Typography variant="heading-sm">
            {i18n.t('check_capture.back_of_check')}
          </Typography>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => handleCapture('rear')}
            style={styles.card}
            testID="check-capture-rear-card"
          >
            {images.rearImage ? (
              <Image
                resizeMode="cover"
                source={{ uri: imageUri(images.rearImage) }}
                style={styles.checkImage}
              />
            ) : null}
            <CameraIcon
              height={24}
              stroke={
                images.rearImage
                  ? theme.sk.color.icon.positive.primary
                  : theme.sk.color.icon.tertiary
              }
              width={24}
            />
            <Typography
              color={
                images.rearImage
                  ? theme.sk.color.text.success
                  : theme.sk.color.text.tertiary
              }
              variant="paragraph-md"
            >
              {images.rearImage
                ? i18n.t('check_capture.tap_to_recapture')
                : i18n.t('check_capture.tap_to_capture')}
            </Typography>
          </TouchableOpacity>
          {rearError ? (
            <View style={styles.errorRow}>
              <AlertIcon
                height={16}
                stroke={theme.sk.color.icon.negative.primary}
                width={16}
              />
              <Typography
                color={theme.sk.color.text.error}
                variant="paragraph-sm"
              >
                {rearError}
              </Typography>
            </View>
          ) : null}
        </View>
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          disabled={!bothSidesCaptured}
          onPress={onUseCheck}
          trackingData={{ name: 'check_capture.use_this_check_button' }}
        >
          {i18n.t('check_capture.use_this_check')}
        </Button>
        <Typography
          color={theme.sk.color.text.disabled}
          textAlign="center"
          variant="paragraph-sm"
        >
          {i18n.t('check_capture.powered_by_mitek')}
        </Typography>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  buttonContainer: {
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  card: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderStyle: 'dashed',
    borderWidth: 2,
    gap: theme.sk.spacing.spaceSmall,
    justifyContent: 'center',
    minHeight: 180,
    overflow: 'hidden',
  },
  checkImage: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: theme.sk.radii.radiusMedium - 2,
  },
  errorRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  scrollContent: {
    gap: theme.sk.spacing.spaceMedium,
    padding: theme.sk.spacing.spaceMedium,
  },
  section: {
    gap: theme.sk.spacing.spaceSmall,
  },
}));
